import { forwardRef, useMemo, useState } from 'react';
import { SearchInput, SimpleSpinner, UserAvatar } from 'src/components';
import { RoleDropdown } from 'src/components/orgSettings/users/RoleDropdown';
import { useGetUsersByRoles } from 'src/hooks';
import type { OrgMember } from 'src/types';

type Props = {
  handleClickPrimaryAction: () => void;
  selectedApprover: OrgMember | null;
  setSelectedApprover: (approver: OrgMember) => void;
};

export const NamedApproversView = forwardRef<HTMLDivElement, Props>(
  (
    { handleClickPrimaryAction, selectedApprover, setSelectedApprover },
    ref,
  ) => {
    const [search, setSearch] = useState<string>('');
    const { data: approvers, isPending } = useGetUsersByRoles([
      'admin',
      'approver',
    ]);

    const displayedApprovers = useMemo(() => {
      if (!approvers) {
        return [];
      }

      const searchTerm = search.trim().toLowerCase();

      // all should show if no search term
      if (!searchTerm) {
        return approvers;
      }

      // search term exists, filter out non-hits
      return approvers.filter((approver) => {
        return (
          approver.email.toLowerCase().includes(searchTerm) ||
          approver.name?.toLowerCase().includes(searchTerm) ||
          approver.role.toLowerCase().includes(searchTerm)
        );
      });
    }, [approvers, search]);

    // select the only displayed approver if there is only one, otherwise do nothing
    const handlePressEnter = () => {
      if (displayedApprovers.length === 1) {
        setSelectedApprover(displayedApprovers[0]);
      }
    };

    return (
      <>
        <h4 className='heading-300 text-primary-dark'>Select Approver</h4>
        <p className='text-300 text-pretty text-center text-primary-dark'>
          The approver you select will receive an email notification to review
          your cart.
        </p>
        {isPending ? (
          <div className='flex w-full flex-col items-center justify-center gap-2'>
            <SimpleSpinner
              customHeight='h-20'
              customWidth='w-20'
              customPosition='relative'
            />
          </div>
        ) : (
          <div
            ref={
              selectedApprover === null
                ? (ref as React.Ref<HTMLDivElement>)
                : undefined
            }
            className='flex w-full flex-col items-center justify-center gap-2'
          >
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder='Search for an approver'
              focusPlaceholder='Type to search for an approver'
              onEnterPress={handlePressEnter}
              className='px-0.5'
              bgColor='bg-offWhite group-hover:bg-offWhite/60 group-hover:focus:bg-offWhite'
              autoFocus={true}
            />
            <div className='max-h-[300px] min-h-[300px] w-full overflow-y-scroll p-0.5'>
              <ul className='flex w-full flex-col items-center justify-center gap-2'>
                {displayedApprovers.length > 0 ? (
                  displayedApprovers.map((approver, i) => (
                    <li
                      key={`named-approver-${approver.id}-${i}`}
                      className={`${selectedApprover?.id === approver.id && 'ring-2 ring-primary'} flex min-h-12 w-full flex-row flex-wrap items-center justify-center gap-3 rounded-lg bg-offWhite p-2 transition-all active:scale-[98%] md:flex-nowrap`}
                      onClick={() => setSelectedApprover(approver)}
                    >
                      <UserAvatar
                        name={approver.name || approver.email.toUpperCase()}
                      />
                      <span className='text-100 text-left'>
                        {approver.email}
                      </span>
                      <span className='text-100 mt-0.5 grow truncate text-left text-xs text-primary-dark/40'>
                        {approver.name}
                      </span>
                      <RoleDropdown
                        showDropdown={false}
                        handleShowDropdown={() => {}}
                        isPending={false}
                        displayPending={false}
                        role={approver.role}
                        dropdownId={`named-approver-role-dropdown-${approver.id}`}
                        userId={approver.id}
                        excludeDropdownSpace={true}
                        disableDropdown={true}
                      />
                    </li>
                  ))
                ) : (
                  <li className='flex h-12 w-full flex-row flex-nowrap items-center justify-center gap-3 rounded-lg border-2 border-transparent bg-offWhite p-2'>
                    <span className='text-100 grow text-center text-rose-500'>
                      No approvers found. Try refining your search terms.
                    </span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}
        <button
          disabled={isPending || selectedApprover === null}
          className='button-primary h-12'
          onClick={handleClickPrimaryAction}
          ref={
            selectedApprover !== null
              ? (ref as React.Ref<HTMLButtonElement>)
              : undefined
          }
        >
          Send for Approval
        </button>
      </>
    );
  },
);
